import { supabase } from "@/lib/supabase";
import type { BuiltLead } from "@/lib/leadFields";
import { recordActivity } from "@/services/activityLog";
// Canonical LeadStatus + label map live in @myroute/shared so the future
// mobile app can import the same enum without re-declaring it. Re-export
// here so existing `import { LeadStatus } from "@/services/leads"` call
// sites keep working unchanged.
import type { LeadStatus as SharedLeadStatus } from "@myroute/shared";
export { LEAD_STATUS_LABEL, LEAD_STATUSES } from "@myroute/shared";
export type LeadStatus = SharedLeadStatus;

export interface LeadRow extends BuiltLead {
  id: string;
  team_id: string;
  status: LeadStatus;
  /** When the status last changed (UTC). Trigger-maintained — see migration
   *  0001's handle_lead_status_change(). */
  status_changed_at: string;
  /** Who last changed the status. null when the lead was inserted by the
   *  system or the actor was deleted (FK on delete set null). */
  status_changed_by: string | null;
  assigned_to: string | null;
  lat: number | null;
  lng: number | null;
  geocoded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListLeadsParams {
  search?: string;
  source?: string;
  state?: string;
  /** One or more ZIP codes. Separate with commas — multiple values use exact
   *  match via `.in()`; a single partial value uses `ilike` prefix match. */
  zip?: string;
  /** Minimum age in years (inclusive). Converted to a DOB upper-bound. */
  ageMin?: number | null;
  /** Maximum age in years (inclusive). Converted to a DOB lower-bound. */
  ageMax?: number | null;
  status?: LeadStatus | "all";
  /** Filter to leads whose `status_changed_at` is >= this ISO datetime.
   *  Used by the Dashboard click-through so "Sold today" drills into the
   *  exact rows that contributed to that KPI count. */
  changedFrom?: string | null;
  /** Upper bound (exclusive) on status_changed_at. */
  changedTo?: string | null;
  /** Filter to leads whose last status change was performed by this user. */
  changedBy?: string | null;
  /** Minimum annual premium (inclusive, in dollars). */
  premiumMin?: number | null;
  /** Maximum annual premium (inclusive, in dollars). */
  premiumMax?: number | null;
  /** Filter by city name (partial, case-insensitive). */
  city?: string;
  /** Sort order. `created` = newest first (default). `location` = by lat/lng
   *  so geographically close leads appear together. */
  sortBy?: "created" | "location";
  page?: number;
  pageSize?: number;
}

export interface ListLeadsResult {
  rows: LeadRow[];
  total: number;
}

const DEFAULT_PAGE_SIZE = 50;

/**
 * Paginated list of leads scoped by RLS to the caller's team + role.
 * `search` is OR-matched against first_name / last_name / full_name / email / phone.
 */
export async function listLeads(params: ListLeadsParams = {}): Promise<ListLeadsResult> {
  const {
    search, source, state, zip, ageMin, ageMax, status = "all",
    changedFrom, changedTo, changedBy,
    city, premiumMin, premiumMax, sortBy = "created",
    page = 0, pageSize = DEFAULT_PAGE_SIZE,
  } = params;

  const baseOrder = sortBy === "location"
    ? { column: "lat", ascending: true, nullsFirst: false }
    : { column: "created_at", ascending: false };

  let query = supabase
    .from("leads")
    .select(
      "id, team_id, first_name, last_name, full_name, email, phone, phone_alt, address_street, address_city, address_state, address_zip, address_combined, dob, source, notes, custom_fields, status, status_changed_at, status_changed_by, assigned_to, lat, lng, geocoded_at, created_at, updated_at",
      // `exact`, not `planned`: planned uses the Postgres planner's row
      // estimate, which lags behind writes — after a soft-delete UPDATE the
      // stale stats made the displayed total go UP, and fresh imports showed
      // wrong totals until ANALYZE ran.
      { count: "exact" },
    )
    .is("deleted_at", null)
    .order(baseOrder.column, { ascending: baseOrder.ascending, nullsFirst: (baseOrder as { nullsFirst?: boolean }).nullsFirst ?? true });

  // Secondary sort by lng when ordering by location so nearby lat-bands are
  // further clustered west-to-east.
  if (sortBy === "location") {
    query = query.order("lng", { ascending: true, nullsFirst: false });
  }

  if (status !== "all") query = query.eq("status", status);
  if (source && source.trim()) query = query.eq("source", source.trim());
  if (state && state.trim()) query = query.eq("address_state", state.trim());

  // Multi-ZIP: comma/space separated list → exact .in(); single partial → ilike prefix.
  if (zip && zip.trim()) {
    const parts = zip.split(/[,\s]+/).map((z) => z.trim()).filter(Boolean);
    if (parts.length > 1) {
      query = query.in("address_zip", parts);
    } else {
      query = query.ilike("address_zip", `${parts[0]}%`);
    }
  }

  // Age range → DOB bounds (age = floor years since birth).
  if (ageMin != null) {
    const d = new Date();
    d.setFullYear(d.getFullYear() - ageMin);
    query = query.lte("dob", d.toISOString().slice(0, 10));
  }
  if (ageMax != null) {
    const d = new Date();
    d.setFullYear(d.getFullYear() - ageMax - 1);
    // Add 1 day so the upper bound is inclusive for the birth year.
    d.setDate(d.getDate() + 1);
    query = query.gte("dob", d.toISOString().slice(0, 10));
  }

  if (changedFrom) query = query.gte("status_changed_at", changedFrom);
  if (changedTo) query = query.lt("status_changed_at", changedTo);
  if (changedBy) query = query.eq("status_changed_by", changedBy);

  if (city && city.trim()) query = query.ilike("address_city", `%${city.trim()}%`);

  if (premiumMin != null) query = query.gte("household_annual_premium", premiumMin);
  if (premiumMax != null) query = query.lte("household_annual_premium", premiumMax);

  if (search && search.trim()) {
    const q = search.trim().replace(/[,()]/g, " ");
    const like = `%${q}%`;
    query = query.or(
      `first_name.ilike.${like},last_name.ilike.${like},full_name.ilike.${like},email.ilike.${like},phone.ilike.${like},address_city.ilike.${like},address_street.ilike.${like}`,
    );
  }

  const from = page * pageSize;
  const to = from + pageSize - 1;
  const { data, count, error } = await query.range(from, to);
  // PGRST103 = requested range beyond the result set — happens transiently
  // when a bulk delete shrinks the list while the UI is still on a tail
  // page. Treat as an empty page; the list's page-clamp effect resolves it.
  if (error && (error as { code?: string }).code === "PGRST103") {
    return { rows: [], total: count ?? 0 };
  }
  if (error) throw error;

  return {
    rows: (data ?? []) as unknown as LeadRow[],
    total: count ?? 0,
  };
}

/**
 * Distinct values of `address_state` across non-deleted leads in the caller's team (RLS-scoped).
 * Pulls up to 5000 rows column-only and dedupes client-side — fast for typical teams,
 * may miss values for very large teams. Move to an RPC if that becomes a problem.
 */
export async function listLeadStates(): Promise<string[]> {
  const { data, error } = await supabase
    .from("leads")
    .select("address_state")
    .is("deleted_at", null)
    .not("address_state", "is", null)
    .limit(5000);
  if (error) throw error;
  const set = new Set<string>();
  for (const row of data ?? []) {
    const v = (row.address_state ?? "").trim();
    if (v) set.add(v);
  }
  return [...set].sort();
}

/** Distinct values of `source` across non-deleted leads in the caller's team. */
export async function listLeadSources(): Promise<string[]> {
  const { data, error } = await supabase
    .from("leads")
    .select("source")
    .is("deleted_at", null)
    .not("source", "is", null)
    .limit(5000);
  if (error) throw error;
  const set = new Set<string>();
  for (const row of data ?? []) {
    const v = (row.source ?? "").trim();
    if (v) set.add(v);
  }
  return [...set].sort();
}

export interface InsertableLead extends BuiltLead {
  team_id: string;
  import_id: string | null;
  status: LeadStatus;
}

export interface InsertProgress {
  /** Rows inserted so far across all batches that have completed. */
  inserted: number;
  /** Total rows in this import (passed up-front so progress percentage works). */
  total: number;
}

/**
 * Inserts leads in batches, optionally reporting progress. Returns total
 * inserted. Throws on the first failed batch.
 *
 * Batch size 200 is the sweet spot: small enough that any one batch stays
 * within Postgres' parameterized-query limit and supabase-js's serialization
 * budget; large enough that a 10k-row import finishes in ~50 round-trips
 * instead of 10k. For a typical 200ms RTT that's ~10 seconds total.
 *
 * The optional `onProgress` callback fires after each batch commits so the
 * caller can drive a progress bar — Route4Me-style "100 at a time" upload
 * limits are not necessary because the UX shows users what's happening.
 */
export async function insertLeadsBatch(
  rows: InsertableLead[],
  batchSize = 200,
  onProgress?: (p: InsertProgress) => void,
): Promise<{ inserted: number; ids: string[] }> {
  let inserted = 0;
  const total = rows.length;
  const ids: string[] = [];
  for (let i = 0; i < rows.length; i += batchSize) {
    const slice = rows.slice(i, i + batchSize);
    const { data, error, count } = await supabase
      .from("leads")
      .insert(slice, { count: "exact" })
      .select("id");
    if (error) throw error;
    if (data) for (const r of data) ids.push((r as { id: string }).id);
    inserted += count ?? slice.length;
    onProgress?.({ inserted, total });
  }
  return { inserted, ids };
}

/** Soft-delete leads by setting deleted_at/deleted_by. Admin-only via RLS. */
export async function softDeleteLeads(leadIds: string[], deletedBy: string): Promise<number> {
  if (!leadIds.length) return 0;
  const now = new Date().toISOString();
  const { error, count } = await supabase
    .from("leads")
    .update({ deleted_at: now, deleted_by: deletedBy, assigned_to: null, updated_at: now }, { count: "exact" })
    .in("id", leadIds)
    .is("deleted_at", null);
  if (error) throw error;
  const deleted = count ?? 0;
  void recordActivity({
    kind: "lead_delete",
    summary: `Deleted ${deleted} lead${deleted === 1 ? "" : "s"}`,
    metadata: { lead_ids: leadIds.slice(0, 200), requested: leadIds.length, deleted },
  });
  return deleted;
}

/**
 * Bulk-assigns leads to a team member (or unassigns with null). Elevated
 * roles only in practice — leads_update RLS lets admin/route_manager touch
 * any team lead; drivers can only touch their own/unassigned, so a driver
 * calling this can never steal another driver's leads.
 */
export async function assignLeads(leadIds: string[], assignee: string | null): Promise<number> {
  if (!leadIds.length) return 0;
  const { error, count } = await supabase
    .from("leads")
    .update({ assigned_to: assignee, updated_at: new Date().toISOString() }, { count: "exact" })
    .in("id", leadIds)
    .is("deleted_at", null);
  if (error) throw error;
  const affected = count ?? 0;
  void recordActivity({
    kind: "lead_assign",
    summary: assignee
      ? `Assigned ${affected} lead${affected === 1 ? "" : "s"}`
      : `Unassigned ${affected} lead${affected === 1 ? "" : "s"}`,
    metadata: { assignee, lead_ids: leadIds.slice(0, 200), affected },
  });
  return affected;
}

